import React, { useState, useRef, useEffect } from 'react';
import { FiChevronDown, FiSearch } from 'react-icons/fi';

interface DropdownOption {
  value: string;
  label: string;
  isVMIQ?: boolean;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  className?: string;
  searchable?: boolean;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  className = '',
  searchable = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(null);
  const [openUpward, setOpenUpward] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filtrar opciones basado en el término de búsqueda
  const filteredOptions = searchable && searchTerm 
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  useEffect(() => {
    const option = options.find(opt => opt.value === value);
    setSelectedOption(option || null);
  }, [value, options]);

  // Focus en el input cuando se activa el modo de búsqueda
  useEffect(() => {
    if (isSearchMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSearchMode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsSearchMode(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionClick = (option: DropdownOption) => {
    onChange(option.value);
    setIsOpen(false);
    setIsSearchMode(false);
    setSearchTerm('');
  };

  const toggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        if (dropdownRef.current) {
          const rect = dropdownRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - rect.bottom;
          const spaceAbove = rect.top;
          const dropdownHeight = 120; 
          
          setOpenUpward(spaceBelow < dropdownHeight && spaceAbove > dropdownHeight);
        }
        if (searchable) {
          setIsSearchMode(true);
        }
      } else {
        setIsSearchMode(false);
        setSearchTerm('');
      }
      setIsOpen(!isOpen);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setIsSearchMode(false);
      setSearchTerm('');
    }
  };

  return (
    <div className={`custom-dropdown ${className}`} ref={dropdownRef}>
      <div 
        className={`dropdown-trigger ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
        onClick={toggleDropdown}
      >
        {isSearchMode && searchable ? (
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            placeholder="Search Store"
            className="search-input-integrated"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="dropdown-value">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        )}
        <FiChevronDown className={`dropdown-icon ${isOpen ? 'rotated' : ''}`} />
      </div>
      
      {isOpen && (
        <div className={`dropdown-menu ${openUpward ? 'dropdown-menu-up' : ''}`}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`dropdown-option ${option.isVMIQ ? 'vmiq-option' : ''}`}
                onClick={() => handleOptionClick(option)}
              >
                <span className="option-label">{option.label}</span>
                {option.isVMIQ && (
                  <span className="option-vmiq">VMIQ</span>
                )}
              </div>
            ))
          ) : (
            <div className="no-results">
              No data found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;
